/**
 * Phase 8 T-06b §20.C · Model chip shown above every assistant turn.
 *
 * Normally shows `GGJ Reasoning v2` (overridable via VITE_AGENT_MODEL_LABEL).
 * When the Groq circuit breaker is open (rate-limited / edge down), it
 * switches to "Local AI · Ollama" with an amber indicator so the user
 * can tell their local model is responding.
 */

import { useEffect, useState } from 'react';
import { Cpu, Sparkles } from 'lucide-react';
import { isLlamaEdgeBreakerOpen, getLlamaEdgeBreakerReason } from '@core/ai/llamaChat';

export const AGENT_MODEL_LABEL: string =
  (import.meta.env.VITE_AGENT_MODEL_LABEL as string | undefined) ?? 'GGJ Reasoning v2';

interface Props {
  /** Optional step count to include in the hover tooltip. */
  steps?: number;
  /** Optional token count for tooltip. */
  tokens?: number;
}

export function AgentModelChip({ steps, tokens }: Props) {
  const [ollamaActive, setOllamaActive] = useState(() => isLlamaEdgeBreakerOpen());

  // Poll the circuit breaker every 5 s so the chip updates automatically
  // when Groq recovers (breaker resets) or trips again.
  useEffect(() => {
    const id = setInterval(() => setOllamaActive(isLlamaEdgeBreakerOpen()), 5_000);
    return () => clearInterval(id);
  }, []);

  const reason = getLlamaEdgeBreakerReason();
  const label = ollamaActive ? 'Local AI · Ollama' : AGENT_MODEL_LABEL;
  const tooltipBase = ollamaActive
    ? reason === 'rate-limit'
      ? 'Groq rate-limited — using your local Ollama model'
      : 'Groq edge unavailable — using your local Ollama model'
    : AGENT_MODEL_LABEL;
  const tooltipParts: string[] = [tooltipBase];
  if (typeof steps === 'number') tooltipParts.push(`${steps} orchestrator steps`);
  if (typeof tokens === 'number') tooltipParts.push(`${tokens} tokens`);

  if (ollamaActive) {
    return (
      <span
        className="inline-flex items-center gap-1.5 text-[11px] font-medium"
        style={{ color: '#92400E' }}
        title={tooltipParts.join(' · ')}
      >
        <span
          className="inline-flex h-4 w-4 items-center justify-center rounded-full"
          style={{ background: '#FEF3C7', color: '#92400E' }}
          aria-hidden
        >
          <Cpu className="h-2.5 w-2.5" />
        </span>
        {label}
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1.5 text-[11px] font-medium"
      style={{ color: 'var(--vscode-text-secondary)' }}
      title={tooltipParts.join(' · ')}
    >
      <span
        className="inline-flex h-4 w-4 items-center justify-center rounded-full"
        style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
        aria-hidden
      >
        <Sparkles className="h-2.5 w-2.5" />
      </span>
      {label}
    </span>
  );
}
